import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as LeftArrow } from "../../../assets/icons/leftArrow.svg";
import { ReactComponent as Minus } from "../../../assets/icons/minus.svg";
import { ReactComponent as Plus } from "../../../assets/icons/plus.svg";
import { ReactComponent as RightArrow } from "../../../assets/icons/rightArrow.svg";
import {
  mappedTrendFilterType,
  materialsForBackend,
  stylingDetailsForBackend,
  trendFilters,
} from "../../../constants/constants";
import { areArraysDifferentHavingDifferentOrder } from "../../../utils/commonUtils";
import { getTrendFiltersFromSession } from "../../../utils/trendSidebarGraphUtils/graphHelperUtils";
import GraphsSidebarForSnapshot from "../TrendsSnapshot/GraphsSidebarForSnapshot";
import ColorAnalysisSidebar from "./ColorAnalysisSidebar";
import OverallAnalysisSidebar from "./OverallAnalysisSidebar";
import TrendCurvesSidebar from "./TrendCurvesSidebar";
import TrendsInput from "./TrendsInput";
import Dropdown from "../../Common/Dropdown";
import store from "../../../app/store";
import { clearTrendsData } from "../../../app/slices/trendSlice";

interface TrendsProps {
  isTrendOpen: boolean;
  onTrendToggle: () => void;
  productType: string[];
  selectedProductType: string;
  setSelectedProductType: React.Dispatch<React.SetStateAction<string>>;
  materials: string[];
  selectedMaterial: string[];
  setSelectedMaterial: React.Dispatch<React.SetStateAction<string[]>>;
  stylingDetails: string[];
  selectedStylingDetail: string[];
  setSelectedStylingDetail: React.Dispatch<React.SetStateAction<string[]>>;
  runAnalysis: (
    colorForLevel2: string,
    overallFilter: string,
    page: number
  ) => void;
  setAnalysisResultSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  refChange: (value: string) => void;
  startDate: string;
  endDate: string;
  region: string;
  genre: string;
  product: string;
  gender: string;
  brands: string[];
  source: string;
  category: string;
  color: string;
  req_id: string;
  TrendsSidebarSnapshotExpandedView: boolean;
  analysisResultSuccess: boolean;
  ageGroup?: string;
  clear?: boolean;
  setClear?: React.Dispatch<React.SetStateAction<boolean>>;
  colorForLevel2?: string;
  setColorForLevel2?: React.Dispatch<React.SetStateAction<string>>;
  showColorAnalysis?: boolean;
  trendFilterType?: string;
  setTrendFilterType?: React.Dispatch<React.SetStateAction<string>>;
  isBarGraphDataNotAvailable?: boolean;
  setIsBarGraphDataNotAvailable?: React.Dispatch<React.SetStateAction<boolean>>;
  trendsLoading?: boolean;
}

const Trends: React.FC<TrendsProps> = ({
  isTrendOpen,
  onTrendToggle,
  productType,
  selectedProductType,
  setSelectedProductType,
  materials,
  selectedMaterial,
  setSelectedMaterial,
  stylingDetails,
  selectedStylingDetail,
  setSelectedStylingDetail,
  runAnalysis,
  setAnalysisResultSuccess,
  refChange,
  startDate,
  endDate,
  region,
  genre,
  product,
  gender,
  brands,
  source,
  category,
  color,
  req_id,
  TrendsSidebarSnapshotExpandedView,
  analysisResultSuccess,
  ageGroup,
  clear,
  setClear,
  colorForLevel2,
  setColorForLevel2,
  showColorAnalysis,
  trendFilterType,
  setTrendFilterType,
  isBarGraphDataNotAvailable,
  setIsBarGraphDataNotAvailable,
  trendsLoading = false,
}) => {
  const [inputOpen, setInputOpen] = useState(true);
  const [colorAnalysisOpen, setColorAnalysisOpen] = useState(true);
  const [overallAnalysisOpen, setOverallAnalysisOpen] = useState(true);
  const [trendCurvesOpen, setTrendCurvesOpen] = useState(true);
  const [filterChanged, setFilterChanged] = useState(true);
  const [graphsFilterChangedStatus, setGraphsFilterChangedStatus] =
    useState(true);
  const toggleInput = () => {
    if (isBarGraphDataNotAvailable) {
      setOverallAnalysisOpen(true);
      setColorAnalysisOpen(true);
      return;
    } else {
      setOverallAnalysisOpen(false);
      setColorAnalysisOpen(false);
    }
    setInputOpen(!inputOpen);
  };
  const toggleColorAnalysis = () => {
    setColorAnalysisOpen(!colorAnalysisOpen);
  };
  const toggleOverallAnalysis = () => {
    setOverallAnalysisOpen(!overallAnalysisOpen);
  };
  const toggleTrendCurve = () => {
    setTrendCurvesOpen(!trendCurvesOpen);
  };

  useEffect(() => {
    if (clear && setClear) {
      clearAll();
      setClear(false);
    }
  }, [clear]);

  const clearAll = () => {
    setSelectedProductType("");
    setSelectedMaterial([]);
    setSelectedStylingDetail([]);
    setAnalysisResultSuccess(false);
    clearGraphsStates();
    setIsBarGraphDataNotAvailable && setIsBarGraphDataNotAvailable(false);
    setOverallAnalysisOpen(false);
    setColorAnalysisOpen(false);
    sessionStorage.removeItem("trends");
    sessionStorage.removeItem("last_req_id");
    sessionStorage.removeItem("last_Archive_Arr");
    // Clear Redux trends data
    store.dispatch(clearTrendsData());
  };

  const checkFilterChanged = () => {
    const trendFilters = getTrendFiltersFromSession();
    if (!trendFilters) return true;
    // Region
    if (
      (trendFilters.region && trendFilters.region !== region) ||
      (!trendFilters.region && region)
    )
      return true;

    // Genre
    if (
      (trendFilters.genre && trendFilters.genre !== genre) ||
      (!trendFilters.genre && genre)
    )
      return true;

    // Product
    if (
      (trendFilters.product && trendFilters.product !== product) ||
      (!trendFilters.product && product)
    )
      return true;

    // Gender
    if (
      (trendFilters.gender && trendFilters.gender !== gender) ||
      (!trendFilters.gender && gender)
    )
      return true;

    // Brands
    if (trendFilters.brands) {
      const trendFiltersBrands = trendFilters.brands.split(",");
      if (trendFiltersBrands.length !== brands.length) return true;
      if (trendFiltersBrands && Array.isArray(trendFiltersBrands))
        if (areArraysDifferentHavingDifferentOrder(trendFiltersBrands, brands))
          return true;
    } else if (brands.length > 0) return true;

    // Start Date
    if (
      (trendFilters.start_date &&
        trendFilters.start_date.split(" ")[0] !== startDate) ||
      (!trendFilters.start_date && startDate)
    )
      return true;

    // End Date
    if (
      (trendFilters.end_date &&
        trendFilters.end_date.split(" ")[0] !== endDate) ||
      (!trendFilters.end_date && endDate)
    )
      return true;

    // Age Group
    if (
      (trendFilters.age_group && trendFilters.age_group !== ageGroup) ||
      (!trendFilters.age_group && ageGroup)
    )
      return true;

    // Source
    if (
      (trendFilters.source && trendFilters.source !== source) ||
      (!trendFilters.source && source)
    )
      return true;

    // Category
    if (
      (trendFilters.category && trendFilters.category !== category) ||
      (!trendFilters.category && category)
    )
      return true;

    // Color
    if (
      (trendFilters.colors && trendFilters.colors !== color) ||
      (!trendFilters.colors && color)
    )
      return true;

    // Apparel Type/ Product Type
    if (
      trendFilters.apparel_type &&
      trendFilters.apparel_type !== selectedProductType
    ) {
      return true;
    }

    // Materials
    const materialToCheck = Object.entries(materialsForBackend)
      .filter(([key]) =>
        Boolean(trendFilters[key as keyof typeof trendFilters])
      )
      .map(([_, material]) => material.label);
    if (
      areArraysDifferentHavingDifferentOrder(materialToCheck, selectedMaterial)
    )
      return true;

    // Styling Details
    const stylingDetailToCheck = Object.entries(stylingDetailsForBackend)
      .filter(([key]) =>
        Boolean(trendFilters[key as keyof typeof trendFilters])
      )
      .map(([_, stylingDetail]) => stylingDetail.label);
    if (
      areArraysDifferentHavingDifferentOrder(
        stylingDetailToCheck,
        selectedStylingDetail
      )
    )
      return true;

    //default
    return false;
  };

  const checkGraphFiltersChanged = () => {
    const trendFilters = getTrendFiltersFromSession();
    if (!trendFilters) return true;

    //Color Analysis
    if (trendFilters.selected_color && trendFilters.color_analysis_level) {
      if (
        trendFilters.color_analysis_level === "LEVEL2" &&
        "#" + trendFilters.selected_color !== colorForLevel2
      )
        return true;
    } else if (colorForLevel2) return true;

    //Overall Analysis
    if (
      (trendFilters.query_type &&
        trendFilterType &&
        trendFilters.query_type !== mappedTrendFilterType[trendFilterType]) ||
      (!trendFilters.query_type && trendFilterType)
    )
      return true;

    //default
    return false;
  };

  useEffect(() => {
    const filterChangedStatus = checkFilterChanged();
    setFilterChanged(filterChangedStatus);
    if (isBarGraphDataNotAvailable) {
      setOverallAnalysisOpen(true);
      setColorAnalysisOpen(true);
    }
  }, [
    selectedProductType,
    selectedMaterial,
    selectedStylingDetail,
    startDate,
    endDate,
    region,
    genre,
    product,
    gender,
    brands,
    source,
    category,
    color,
    req_id,
    ageGroup,
    sessionStorage.getItem("trends"),
  ]);

  useEffect(() => {
    const status = checkGraphFiltersChanged();
    setGraphsFilterChangedStatus(status);
  }, [colorForLevel2, trendFilterType, req_id, analysisResultSuccess]);

  const clearGraphsStates = () => {
    setColorForLevel2 && setColorForLevel2("");
  };

  useEffect(() => {
    if (overallAnalysisOpen) {
      const div = document.getElementsByClassName("scroll-to-down-div");
      if (div.length > 0) {
        div[0].scrollTo({ top: div[0].scrollHeight, behavior: "smooth" });
      }
    }
  }, [overallAnalysisOpen]);

  const handleRunAnalysis = () => {
    if (trendsLoading) return;

    refChange("");
    // Clear Redux trends data before running new analysis
    store.dispatch(clearTrendsData());

    if (filterChanged) {
      clearGraphsStates();
      setColorAnalysisOpen(true);
      setOverallAnalysisOpen(true);
      setTrendCurvesOpen(true);
      runAnalysis("", trendFilterType || "Most Popular", 1);
    } else {
      setColorAnalysisOpen(true);
      setOverallAnalysisOpen(true);
      setTrendCurvesOpen(true);
      runAnalysis(colorForLevel2 || "", trendFilterType || "Most Popular", 1);
    }
  };

  return (
    <div
      className={`flex flex-col ${TrendsSidebarSnapshotExpandedView ? "bg-dashboard" : "bg-white"} shadow-md transition-all overflow-scroll no-scrollbar duration-500 ease-in-out ${
        isTrendOpen ? "w-[450px]" : "w-8"
      } font-poppins`}
    >
      <div className="flex flex-col h-[calc(100vh-130px)] overflow-y-auto scroller scroll-to-down-div overflow-x-hidden">
        {/* Trends */}
        <div>
          <div
            className={`sticky ml-1 top-0  z-10 ${TrendsSidebarSnapshotExpandedView ? "bg-dashboard" : "bg-white"} ease-in-out`}
          >
            <div
              className={`flex items-center text-primary rounded-2xl mb-2 cursor-pointer ${
                isTrendOpen ? "px-1 py-2" : "justify-center"
              }`}
              onClick={onTrendToggle}
            >
              {isTrendOpen ? (
                <div className="flex w-full  items-center">
                  <RightArrow className="text-lg mr-2" />
                  <span className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
                    Trends
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center mt-4">
                  <LeftArrow className="text-lg mb-2" />
                  <div>
                    {"SDNERT".split("").map((letter, index) => (
                      <span
                        key={index}
                        className="block -rotate-90 font-semibold text-secondary"
                        style={{ marginBottom: "-8px" }}
                      >
                        {letter}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trends Input */}
        <div className={`py-2 px-1  space-y-2 ${isTrendOpen ? "" : "hidden"}`}>
          <div className="flex items-center space-x-2">
            <div className="cursor-pointer" onClick={toggleInput}>
              {inputOpen ? <Minus /> : <Plus />}
            </div>
            <div className="text-md font-semibold text-secondary opacity-100 font-poppins">
              Input
            </div>
          </div>

          <TrendsInput
            inputOpen={inputOpen}
            productType={productType}
            selectedProductType={selectedProductType}
            setSelectedProductType={setSelectedProductType}
            materials={materials}
            selectedMaterial={selectedMaterial}
            setSelectedMaterial={setSelectedMaterial}
            stylingDetails={stylingDetails}
            selectedStylingDetail={selectedStylingDetail}
            setSelectedStylingDetail={setSelectedStylingDetail}
            trendsSidebarSnapshotExpandedView={
              TrendsSidebarSnapshotExpandedView
            }
          />
        </div>

        {/* Color Analysis */}
        {!TrendsSidebarSnapshotExpandedView && (
          <div
            className={` space-y-2 ${isTrendOpen ? "" : "hidden"} mb-1 relative`}
          >
            {((analysisResultSuccess && showColorAnalysis) ||
              !analysisResultSuccess) && (
              <div className="flex items-center justify-between px-1 w-full">
                <div className="flex">
                  <div className="cursor-pointer" onClick={toggleColorAnalysis}>
                    {colorAnalysisOpen ||
                    (analysisResultSuccess && isBarGraphDataNotAvailable) ? (
                      <Minus />
                    ) : (
                      <Plus />
                    )}
                  </div>
                  <div className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
                    Color Analysis
                  </div>
                </div>
              </div>
            )}
            {colorAnalysisOpen && (
              <div>
                {analysisResultSuccess && trendFilterType ? (
                  <>
                    {showColorAnalysis && (
                      <>
                        <ColorAnalysisSidebar
                          trendFilterType={trendFilterType}
                          colorForLevel2={colorForLevel2}
                          setColorForLevel2={setColorForLevel2}
                          isBarGraphDataNotAvailable={
                            isBarGraphDataNotAvailable
                          }
                        />
                        <div className="border-b-2 border-light my-4"></div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-sm text-center">
                      Select above trend options
                    </div>
                    <div className="border-b-2 border-light mt-2"></div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* OverallAnalysis */}
        {!TrendsSidebarSnapshotExpandedView && (
          <div
            className={` space-y-2 ${isTrendOpen ? "" : "hidden"} my-1 relative`}
          >
            <div className="flex items-center justify-between px-1">
              <div className="flex">
                <div className="cursor-pointer" onClick={toggleOverallAnalysis}>
                  {overallAnalysisOpen ||
                  (analysisResultSuccess && isBarGraphDataNotAvailable) ? (
                    <Minus />
                  ) : (
                    <Plus />
                  )}
                </div>
                <div className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
                  Overall Analysis
                </div>
              </div>
            </div>
            {overallAnalysisOpen && (
              <>
                {analysisResultSuccess && (
                  <div className="ml-2.5 mr-2.5">
                    <div className="text-sm text-secondary mb-1">Filter By</div>
                    <Dropdown
                      label={trendFilterType || "Select type"}
                      options={trendFilters}
                      onSelect={option => {
                        if (option !== trendFilterType) {
                          setTrendFilterType && setTrendFilterType(option);
                        }
                      }}
                      trendSnapshotView={TrendsSidebarSnapshotExpandedView}
                    />
                  </div>
                )}
                {trendFilterType && (
                  <div>
                    {analysisResultSuccess ? (
                      <>
                        <OverallAnalysisSidebar
                          trendFilterType={trendFilterType}
                          selectedMaterials={selectedMaterial}
                          isBarGraphDataNotAvailable={
                            isBarGraphDataNotAvailable
                          }
                        />
                        <div className="border-b-2 border-light mt-2"></div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-center">
                          Select above trend options
                        </div>
                        <div className="border-b-2 border-light mt-2"></div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!TrendsSidebarSnapshotExpandedView && (
          <div
            className={` space-y-2 ${isTrendOpen ? "" : "hidden"} my-1 relative`}
          >
            <div className="flex items-center justify-between px-1">
              <div className="flex">
                <div className="cursor-pointer" onClick={toggleTrendCurve}>
                  {trendCurvesOpen ? <Minus /> : <Plus />}
                </div>
                <div className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
                  Trend Curve
                </div>
              </div>
            </div>
            {trendCurvesOpen && (
              <div>
                {analysisResultSuccess ? (
                  <TrendCurvesSidebar />
                ) : (
                  <div className="text-sm text-center">
                    Select above trend options
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {TrendsSidebarSnapshotExpandedView && isTrendOpen && (
          <GraphsSidebarForSnapshot />
        )}
      </div>
      <div
        className={`flex justify-evenly h-[50px] items-center border-t border-[#E9E9E9] ${isTrendOpen ? "" : "hidden"}`}
      >
        <button
          className="text-md bg-black disabled:bg-gray px-10 py-1 rounded-full font-[700]  text-white disabled:text-[#9E9E9E]"
          disabled={
            trendsLoading ||
            !(filterChanged || graphsFilterChangedStatus) ||
            !selectedProductType ||
            TrendsSidebarSnapshotExpandedView ||
            (selectedMaterial.length == 0 && selectedStylingDetail.length == 0)
          }
          onClick={handleRunAnalysis}
        >
          Run Analysis
        </button>
        <button
          className="text-black disabled:text-[#9E9E9E] font-[700] text-sm"
          onClick={clearAll}
          disabled={
            !(
              selectedProductType ||
              selectedMaterial.length ||
              selectedStylingDetail.length ||
              analysisResultSuccess
            ) || TrendsSidebarSnapshotExpandedView
          }
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

export default Trends;
